import "webdatarocks/webdatarocks.min.css";
import React, { useEffect, useRef, useState } from "react";
import WebDataRocks from "webdatarocks";
import localization from "./lang.json";
import "../pivot/Pivot.css";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { Modal, Input, Select, Button, message, Spin } from "antd";
import { SaveOutlined, FolderOpenOutlined, FileTextOutlined, ReloadOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../../utils/config";
import { trEquals } from "../../utils/trText";
import axios from "axios";

const { Option } = Select;

const InlinePivotChart = ({ pivotId, darkMode }) => {
    const { getAuthHeaders } = useAuth();
    const chartRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [pivotConfig, setPivotConfig] = useState(null);
    const [googleChartsLoaded, setGoogleChartsLoaded] = useState(false);

    useEffect(() => {
        const initializeGoogleCharts = () => {
            if (window.google && window.google.charts) {
                window.google.charts.load('current', { 'packages': ['corechart'] });
                window.google.charts.setOnLoadCallback(() => {
                    setGoogleChartsLoaded(true);
                });
            } else {
                const checkGoogleCharts = setInterval(() => {
                    if (window.google && window.google.charts) {
                        clearInterval(checkGoogleCharts);
                        window.google.charts.load('current', { 'packages': ['corechart'] });
                        window.google.charts.setOnLoadCallback(() => {
                            setGoogleChartsLoaded(true);
                        });
                    }
                }, 200);
                setTimeout(() => clearInterval(checkGoogleCharts), 15000);
            }
        };

        initializeGoogleCharts();
        const timeoutId = setTimeout(initializeGoogleCharts, 1000);

        return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (pivotId) {
            loadPivotData();
        }
    }, [pivotId]);

    useEffect(() => {
        if (googleChartsLoaded && chartData && pivotConfig && chartRef.current) {
            drawChart();
        }
    }, [googleChartsLoaded, chartData, pivotConfig, darkMode]);

    const loadPivotData = async () => {
        setLoading(true);
        try {
            const configResponse = await axios.get(`${API_BASE_URL}/pivot/get/${pivotId}`, {
                headers: getAuthHeaders()
            });

            if (configResponse.data.success && configResponse.data.pivot) {
                const pivot = configResponse.data.pivot;
                setPivotConfig(pivot);

                const snapshot = pivot.chart_snapshot;
                if (snapshot && Array.isArray(snapshot.data) && snapshot.data.length > 1) {
                    setChartData(snapshot.data);
                    return;
                }
            }
        } catch (error) {
            console.error("Error loading pivot:", error);
            message.error("Grafik yapılandırması yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    const drawChart = () => {
        if (!window.google || !window.google.visualization || !chartRef.current || !chartData) {
            return;
        }

        try {
            const data = window.google.visualization.arrayToDataTable(chartData);

            const isMobile = window.innerWidth < 768;

            const options = {
                title: pivotConfig.title,
                height: isMobile ? 280 : 350,
                backgroundColor: darkMode ? '#1f1f1f' : '#ffffff',
                titleTextStyle: {
                    color: darkMode ? '#ffffff' : '#000000',
                    fontSize: isMobile ? 13 : 16
                },
                legend: {
                    position: isMobile ? 'bottom' : 'right',
                    textStyle: {
                        color: darkMode ? '#ffffff' : '#000000',
                        fontSize: isMobile ? 11 : 12
                    }
                },
                chartArea: {
                    width: isMobile ? '85%' : '75%',
                    height: isMobile ? '60%' : '70%'
                }
            };

            const chartType = (pivotConfig.chart_snapshot?.type) || pivotConfig.chart_type;

            if (chartType === 'pie') {
                options.pieSliceText = 'value';
                options.sliceVisibilityThreshold = 0;
            } else if (chartType === 'bar' || chartType === 'bar_h') {
                options.hAxis = {
                    textStyle: { color: darkMode ? '#ffffff' : '#000000' },
                    gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
                };
                options.vAxis = {
                    textStyle: { color: darkMode ? '#ffffff' : '#000000' }
                };
            } else if (chartType === 'column') {
                options.vAxis = {
                    textStyle: { color: darkMode ? '#ffffff' : '#000000' },
                    gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
                };
                options.hAxis = {
                    textStyle: { color: darkMode ? '#ffffff' : '#000000' }
                };
            } else if (chartType === 'line' || chartType === 'area') {
                options.vAxis = {
                    textStyle: { color: darkMode ? '#ffffff' : '#000000' },
                    gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
                };
                options.hAxis = {
                    textStyle: { color: darkMode ? '#ffffff' : '#000000' },
                    gridlines: { color: darkMode ? '#404040' : '#e0e0e0' }
                };
            }

            let chart;
            switch (chartType) {
                case 'pie':
                    chart = new window.google.visualization.PieChart(chartRef.current);
                    break;
                case 'bar':
                case 'bar_h':
                    chart = new window.google.visualization.BarChart(chartRef.current);
                    break;
                case 'column':
                    chart = new window.google.visualization.ColumnChart(chartRef.current);
                    break;
                case 'line':
                    chart = new window.google.visualization.LineChart(chartRef.current);
                    break;
                case 'area':
                    chart = new window.google.visualization.AreaChart(chartRef.current);
                    break;
                default:
                    chart = new window.google.visualization.ColumnChart(chartRef.current);
            }

            chart.draw(data, options);
        } catch (error) {
            console.error("Error drawing chart:", error);
            message.error("Grafik çizilemedi");
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
                <div style={{ marginTop: '10px' }}>Grafik yükleniyor...</div>
            </div>
        );
    }

    if (!chartData) {
        return (
            <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
                <FileTextOutlined style={{ fontSize: '24px', marginBottom: '10px' }} />
                <div>Veri bulunamadı</div>
                <div style={{ fontSize: '12px', marginTop: '5px' }}>
                    {pivotConfig ? `SQL: ${pivotConfig.sql_query}` : 'Konfigürasyon yükleniyor...'}
                </div>
                <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={loadPivotData}
                    style={{ marginTop: '10px' }}
                >
                    Yeniden Dene
                </Button>
            </div>
        );
    }

    const isMobile = window.innerWidth < 768;

    return (
        <div style={{
            padding: isMobile ? '5px' : '10px',
            backgroundColor: darkMode ? '#1f1f1f' : '#ffffff',
            borderRadius: '8px'
        }}>
            <div
                ref={chartRef}
                style={{
                    width: '100%',
                    minHeight: isMobile ? '300px' : '400px',
                    backgroundColor: darkMode ? '#1f1f1f' : '#ffffff'
                }}
            />
        </div>
    );
};


function PivotPage() {
    const pivotRef = useRef(null);
    const chartPreviewRef = useRef(null);
    const { darkMode } = useTheme();
    const { getAuthHeaders } = useAuth();
    const [pageTitle, setPageTitle] = useState("Pivot Tablo");
    const [hasData, setHasData] = useState(false);
    const [pivotData, setPivotData] = useState(null);
    const [saveModalVisible, setSaveModalVisible] = useState(false);
    const [loadModalVisible, setLoadModalVisible] = useState(false);
    const [saveTitle, setSaveTitle] = useState("");
    const [chartType, setChartType] = useState("flat");
    const [saving, setSaving] = useState(false);
    const [savedPivots, setSavedPivots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [googleChartsLoaded, setGoogleChartsLoaded] = useState(false);
    const [showChartPreview, setShowChartPreview] = useState(false);

    useEffect(() => {
        const initializeGoogleCharts = () => {
            if (window.google && window.google.charts) {
                window.google.charts.load("current", { packages: ["corechart"] });
                window.google.charts.setOnLoadCallback(() => setGoogleChartsLoaded(true));
            } else {
                const timer = setInterval(() => {
                    if (window.google && window.google.charts) {
                        clearInterval(timer);
                        window.google.charts.load("current", { packages: ["corechart"] });
                        window.google.charts.setOnLoadCallback(() => setGoogleChartsLoaded(true));
                    }
                }, 200);
                setTimeout(() => clearInterval(timer), 15000);
            }
        };
        initializeGoogleCharts();
    }, []);

    const applyDarkModeStyles = () => {
        if (!darkMode) return;

        const styleId = 'webdatarocks-dark-mode';
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        styleElement.textContent = `
            #wdr-component,
            #wdr-component * {
                background-color: #1a1a1a !important;
                color: #f0f0f0 !important;
            }
            
            .wdr-toolbar {
                background-color: #2d2d2d !important;
                border-bottom: 1px solid #555555 !important;
            }
            
            .wdr-toolbar li a {
                color: #f0f0f0 !important;
                background-color: transparent !important;
            }
            
            .wdr-toolbar li a:hover {
                color: #4dabf7 !important;
                background-color: #3c3c3c !important;
            }
            
            .wdr-toolbar li span {
                color: #b0b0b0 !important;
            }
            
            .wdr-header {
                background-color: #2d2d2d !important;
                color: #f0f0f0 !important;
                border-color: #555555 !important;
            }
            
            .wdr-row {
                background-color: #1a1a1a !important;
                color: #f0f0f0 !important;
            }
            
            .wdr-row:nth-child(even) {
                background-color: #2d2d2d !important;
            }
            
            .wdr-cell {
                background-color: inherit !important;
                color: #f0f0f0 !important;
                border-color: #555555 !important;
            }
            
            .wdr-ui-btn {
                background-color: #2d2d2d !important;
                color: #f0f0f0 !important;
                border: 1px solid #555555 !important;
            }
            
            .wdr-ui-btn:hover {
                background-color: #3c3c3c !important;
                color: #4dabf7 !important;
            }
            
            .wdr-ui-dropdown {
                background-color: #1a1a1a !important;
                border: 1px solid #555555 !important;
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-dropdown-item {
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-dropdown-item:hover {
                background-color: #3c3c3c !important;
            }
            
            .wdr-ui-input {
                background-color: #1a1a1a !important;
                border: 1px solid #555555 !important;
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-modal {
                background-color: #1a1a1a !important;
                border: 1px solid #555555 !important;
            }
            
            .wdr-ui-modal-header {
                background-color: #2d2d2d !important;
                border-bottom: 1px solid #555555 !important;
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-modal-body {
                background-color: #1a1a1a !important;
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-fieldlist {
                background-color: #1a1a1a !important;
                border: 1px solid #555555 !important;
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-fieldlist-item {
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-fieldlist-item:hover {
                background-color: #3c3c3c !important;
            }
            
            .wdr-ui-contextmenu {
                background-color: #1a1a1a !important;
                border: 1px solid #555555 !important;
            }
            
            .wdr-ui-contextmenu-item {
                color: #f0f0f0 !important;
            }
            
            .wdr-ui-contextmenu-item:hover {
                background-color: #3c3c3c !important;
            }
            
            .wdr-table-container table,
            .wdr-table-container th,
            .wdr-table-container td {
                border-color: #555555 !important;
            }
            
            .wdr-table-container::-webkit-scrollbar {
                width: 8px;
                height: 8px;
            }
            
            .wdr-table-container::-webkit-scrollbar-track {
                background: #2d2d2d !important;
            }
            
            .wdr-table-container::-webkit-scrollbar-thumb {
                background: #555555 !important;
                border-radius: 4px;
            }
            
            .wdr-table-container::-webkit-scrollbar-thumb:hover {
                background: #b0b0b0 !important;
            }
        `;
    };

    const removeDarkModeStyles = () => {
        const styleElement = document.getElementById('webdatarocks-dark-mode');
        if (styleElement) {
            styleElement.remove();
        }
    };

    useEffect(() => {
        if (darkMode) {
            applyDarkModeStyles();
        } else {
            removeDarkModeStyles();
        }

        return () => {
            removeDarkModeStyles();
        };
    }, [darkMode]);

    useEffect(() => {
        let loadedData = null;
        try {
            const storedData = sessionStorage.getItem('pivotData');

            if (storedData) {
                loadedData = JSON.parse(storedData);
                setPageTitle(loadedData.title || "Pivot Tablo");
                setPivotData(loadedData);
                setHasData(true);
            }
        } catch (error) {
            console.error("Error loading data from sessionStorage:", error);
            setHasData(false);
        }

        if (!loadedData) {
            const urlParams = new URLSearchParams(window.location.search);
            const dataParam = urlParams.get('data');

            if (dataParam) {
                try {
                    loadedData = JSON.parse(decodeURIComponent(dataParam));
                    setPageTitle(loadedData.title || "Pivot Tablo");
                    setPivotData(loadedData);
                    setHasData(true);
                } catch (error) {
                    console.error("Error parsing pivot data from URL:", error);
                    setHasData(false);
                }
            }
        }

        if (!loadedData) {
            console.warn("No data found for pivot initialization");
            setHasData(false);
        }
    }, []);

    useEffect(() => {
        if (!hasData || !pivotData) return;

        const initializePivot = () => {
            const container = document.getElementById("wdr-component");
            if (!container) {
                setTimeout(initializePivot, 100);
                return;
            }
            const dataSource = pivotData.data;

            if (!dataSource || !Array.isArray(dataSource) || dataSource.length === 0) {
                console.error("No valid data found for pivot initialization");
                setHasData(false);
                return;
            }

            try {
                const storedChartType = pivotData.chart_type || chartType;
                const reportConfig = {
                    dataSource: { data: dataSource },
                    localization: localization,
                    slice: {
                        rows: [],
                        columns: [],
                        measures: [],
                    },
                    options: {
                        grid: {
                            type: "classic",
                            showTotals: "on",
                            showGrandTotals: "on"
                        }
                    }
                };

                if (storedChartType && storedChartType !== "flat") {
                    reportConfig.options.chart = {
                        type: storedChartType,
                        title: pageTitle || "Grafik",
                        showLegend: true,
                        showValues: true
                    };
                    setChartType(storedChartType);
                }

                const pivot = new WebDataRocks({
                    container: "#wdr-component",
                    toolbar: true,
                    customizeToolbar: function (toolbar) {
                        toolbar.getTabs().forEach((tab) => {
                            tab.getItems().forEach((item) => {
                                if (item.id === "wdr-tab-save") {
                                    tab.removeItem(item.id);
                                }
                            });
                        });
                    },
                    report: reportConfig,
                    width: "100%",
                    height: "100%",
                });

                pivot.on("reportcomplete", () => {
                    setTimeout(() => {
                        if (darkMode) {
                            applyDarkModeStyles();
                        }
                        if (storedChartType && storedChartType !== "flat") {
                            if (typeof pivot.showCharts === 'function') {
                                pivot.showCharts();
                            }
                            applyChartType();
                        }
                    }, 100);
                });

                // Live update preview when user changes the report (drag/drop fields, filters, etc.)
                pivot.on("reportchange", () => {
                    if (!pivotRef.current || chartType === "flat") return;
                    const desiredType = chartType === "flat" ? "column" : chartType;
                    if (pivot.googlecharts && typeof pivot.googlecharts.getData === "function") {
                        pivot.googlecharts.getData(
                            { type: desiredType },
                            (gcData) => drawChartPreview(gcData),
                            (gcData) => drawChartPreview(gcData)
                        );
                    }
                });

                pivot.on("error", (error) => {
                    console.error("Pivot error:", error);
                    setHasData(false);
                });

                pivotRef.current = pivot;

            } catch (pivotError) {
                console.error("Error initializing pivot table:", pivotError);
                setHasData(false);
            }
        };

        initializePivot();
    }, [hasData, pivotData]);

    useEffect(() => {
        if (pivotRef.current) {
            if (darkMode) {
                applyDarkModeStyles();
            } else {
                removeDarkModeStyles();
            }
        }
    }, [darkMode]);

    const handleSavePivot = async () => {
        if (!saveTitle.trim()) {
            message.error("Lütfen bir başlık girin");
            return;
        }

        setSaving(true);
        try {
            const pivot = pivotRef.current;
            let sliceConfig = null;
            let chartSnapshot = null;

            if (pivot) {
                try {
                    sliceConfig = pivot.getReport().slice;
                    if (sliceConfig && pivotData.data && pivotData.data.length > 0) {
                        const dataKeys = Object.keys(pivotData.data[0]);
                        if (sliceConfig.rows && sliceConfig.rows.length > 0) {
                            const rowField = sliceConfig.rows[0].uniqueName;
                            const matchingKey = dataKeys.find(key =>
                                trEquals(key, rowField)
                            );
                            if (matchingKey) {
                                sliceConfig.rows[0].uniqueName = matchingKey;
                            }
                        }
                        if (sliceConfig.measures && sliceConfig.measures.length > 0) {
                            sliceConfig.measures.forEach((measure, index) => {
                                const measureField = measure.uniqueName;
                                const matchingKey = dataKeys.find(key =>
                                    trEquals(key, measureField)
                                );
                                if (matchingKey) {
                                    sliceConfig.measures[index].uniqueName = matchingKey;
                                }
                            });
                        }
                    }
                } catch (e) {
                    console.warn("Could not get slice config:", e);
                }
            }

            if (pivot && typeof pivot.googlecharts?.getData === 'function') {
                try {
                    const desiredType = chartType === 'flat' ? 'column' : chartType;
                    chartSnapshot = await new Promise((resolve) => {
                        pivot.googlecharts.getData({ type: desiredType }, (gcData) => {
                            try {
                                const MAX_ROWS = 1000;
                                const trimmed = Array.isArray(gcData?.data) ? [
                                    ...(gcData.data.slice(0, 1)),
                                    ...(gcData.data.slice(1, MAX_ROWS + 1))
                                ] : null;
                                resolve({
                                    data: trimmed,
                                    options: gcData?.options || { title: pageTitle || 'Grafik' },
                                    type: desiredType
                                });
                            } catch {
                                resolve(null);
                            }
                        }, () => resolve(null));
                    });
                } catch (e) {
                    chartSnapshot = null;
                }
            }

            let sqlQuery = pivotData?.sql_query || "";
            const payload = {
                title: saveTitle,
                sql_query: sqlQuery,
                chart_type: chartType,
                slice_config: sliceConfig || null,
                chart_snapshot: chartSnapshot || null
            };
            const response = await axios.post(`${API_BASE_URL}/pivot/save`, payload, {
                headers: getAuthHeaders()
            });

            if (response.data.success) {
                message.success("Pivot tablo başarıyla kaydedildi!");
                setSaveModalVisible(false);
                setSaveTitle("");
                setChartType("flat");
            }
        } catch (error) {
            console.error("Error saving pivot:", error);
            message.error(error.response?.data?.detail || "Pivot kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    const loadSavedPivots = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/pivot/my-pivots`, {
                headers: getAuthHeaders(),
                params: { limit: 50 }
            });
            setSavedPivots(response.data || []);
            setLoadModalVisible(true);
        } catch (error) {
            console.error("Error loading saved pivots:", error);
            message.error("Kaydedilmiş pivot'lar yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    const loadPivotById = async (pivotId) => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/pivot/get/${pivotId}`, {
                headers: getAuthHeaders()
            });

            if (response.data.success && response.data.pivot) {
                const pivot = response.data.pivot;
                setLoadModalVisible(false);
            }
        } catch (error) {
            console.error("Error loading pivot:", error);
            message.error("Pivot yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    const applyChartType = () => {
        if (!pivotRef.current) return;

        try {
            const pivot = pivotRef.current;
            const currentReport = pivot.getReport();
            if (!currentReport.options) {
                currentReport.options = {};
            }

            if (chartType === "flat") {
                if (currentReport.options.chart) {
                    delete currentReport.options.chart;
                }
                pivot.setReport(currentReport);
                pivot.refresh();
                if (typeof pivot.hideCharts === "function") {
                    pivot.hideCharts();
                }
                setShowChartPreview(false);
            } else {
                currentReport.options.chart = {
                    type: chartType,
                    title: pageTitle || "Grafik",
                    showFilter: true,
                    showLegend: true,
                    showMeasures: true
                };
                pivot.setReport(currentReport);
                pivot.refresh();
                if (typeof pivot.showCharts === "function") {
                    pivot.showCharts();
                }
                setShowChartPreview(true);
                // small delay to ensure report refresh is applied before asking data
                setTimeout(() => {
                    if (pivot.googlecharts && typeof pivot.googlecharts.getData === "function") {
                        const desiredType = chartType === "flat" ? "column" : chartType;
                        pivot.googlecharts.getData(
                            { type: desiredType },
                            (gcData) => drawChartPreview(gcData),
                            (gcData) => drawChartPreview(gcData)
                        );
                    }
                }, 200);
            }
        } catch (error) {
            console.warn("Error applying chart type:", error);
            console.error(error);
        }
    };

    const drawChartPreview = (gcData) => {
        if (!googleChartsLoaded || !chartPreviewRef.current || !gcData || !Array.isArray(gcData.data)) {
            return;
        }
        try {
            const data = window.google.visualization.arrayToDataTable(gcData.data);

            const isMobile = window.innerWidth < 768;
            const options = {
                ...(gcData.options || {}),
                backgroundColor: darkMode ? "#1f1f1f" : "#ffffff",
                titleTextStyle: {
                    color: darkMode ? "#ffffff" : "#000000",
                    fontSize: isMobile ? 13 : 16,
                },
                legend: {
                    position: isMobile ? "bottom" : "right",
                    textStyle: { color: darkMode ? "#ffffff" : "#000000", fontSize: isMobile ? 11 : 12 },
                },
                chartArea: { width: isMobile ? "85%" : "75%", height: isMobile ? "60%" : "70%" },
            };

            const type = (gcData.type || chartType || "column").toLowerCase();
            let chart;
            if (type === "pie") chart = new window.google.visualization.PieChart(chartPreviewRef.current);
            else if (type === "bar" || type === "bar_h") chart = new window.google.visualization.BarChart(chartPreviewRef.current);
            else if (type === "line") chart = new window.google.visualization.LineChart(chartPreviewRef.current);
            else if (type === "area") chart = new window.google.visualization.AreaChart(chartPreviewRef.current);
            else chart = new window.google.visualization.ColumnChart(chartPreviewRef.current);

            chart.draw(data, options);
        } catch (e) {
            // no-op: preview is best-effort
        }
    };

    useEffect(() => {
        if (!pivotRef.current || chartType === "flat" || !hasData) return;
        if (!googleChartsLoaded) return;
        const pivot = pivotRef.current;
        if (pivot.googlecharts && typeof pivot.googlecharts.getData === "function") {
            const desiredType = chartType === "flat" ? "column" : chartType;
            pivot.googlecharts.getData(
                { type: desiredType },
                (gcData) => drawChartPreview(gcData),
                (gcData) => drawChartPreview(gcData)
            );
        }
    }, [googleChartsLoaded, darkMode, chartType, hasData]);

    useEffect(() => {
        if (pivotRef.current && chartType && hasData) {
            setTimeout(() => {
                applyChartType();
            }, 300);
        }
    }, [chartType, hasData]);

    return (
        <div
            style={{
                height: "100vh",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: darkMode ? "#1a1a1a" : "#ffffff",
                color: darkMode ? "#f0f0f0" : "#000000"
            }}
        >
            <div
                style={{
                    padding: "16px",
                    borderBottom: `1px solid ${darkMode ? "#555555" : "#d9d9d9"}`,
                    backgroundColor: darkMode ? "#2d2d2d" : "#fafafa",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <h2 style={{
                    margin: 0,
                    color: darkMode ? "#f0f0f0" : "#000000",
                    fontSize: "18px",
                    fontWeight: "600"
                }}>
                    {pageTitle}
                </h2>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    {hasData && (
                        <>
                            <Select
                                value={chartType}
                                onChange={(value) => {
                                    setChartType(value);
                                    message.info(`Görünüm değiştiriliyor: ${value === "flat" ? "Tablo" :
                                            value === "pie" ? "Pasta Grafik" :
                                                value === "bar" ? "Bar Grafik" :
                                                    value === "column" ? "Kolon Grafik" :
                                                        value === "line" ? "Çizgi Grafik" :
                                                            value === "area" ? "Alan Grafik" : value
                                        }`);
                                }}
                                style={{ width: 180 }}
                                placeholder="Grafik Tipi Seç"
                            >
                                <Option value="flat">📊 Tablo Görünümü</Option>
                                <Option value="pie">🥧 Pasta Grafik</Option>
                                <Option value="bar_h">📊 Yatay Bar Grafik</Option>
                                <Option value="column">📊 Kolon Grafik</Option>
                                <Option value="line">📈 Çizgi Grafik</Option>
                                <Option value="area">📉 Alan Grafik</Option>
                            </Select>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={() => {
                                    setSaveTitle(pageTitle);
                                    setSaveModalVisible(true);
                                }}
                            >
                                Kaydet
                            </Button>
                        </>
                    )}
                    <Button
                        icon={<FolderOpenOutlined />}
                        onClick={loadSavedPivots}
                        loading={loading}
                    >
                        Pivot'larım
                    </Button>
                </div>
            </div>
            {hasData ? (
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                        backgroundColor: darkMode ? "#1a1a1a" : "#ffffff",
                        color: darkMode ? "#f0f0f0" : "#000000"
                    }}
                >
                    <div
                        id="wdr-component"
                        ref={pivotRef}
                        style={{
                            flex: showChartPreview ? "1 1 60%" : "1 1 100%",
                            minWidth: "min(480px, 100%)",
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: darkMode ? "#1a1a1a" : "#ffffff",
                            color: darkMode ? "#f0f0f0" : "#000"
                        }}
                    />
                    {showChartPreview && (
                        <div
                            style={{
                                flex: "0 1 40%",
                                minWidth: "min(360px, 100%)",
                                maxWidth: 720,
                                borderLeft: `1px solid ${darkMode ? "#555555" : "#d9d9d9"}`,
                                backgroundColor: darkMode ? "#1a1a1a" : "#ffffff",
                                padding: "12px 16px",
                                display: "flex",
                                flexDirection: "column"
                            }}
                        >
                            <div style={{
                                color: darkMode ? "#f0f0f0" : "#000000",
                                fontWeight: 500,
                                marginBottom: 8
                            }}>
                                Canlı Önizleme
                            </div>
                            <div
                                ref={chartPreviewRef}
                                style={{
                                    width: "100%",
                                    minHeight: 340,
                                    flex: 1,
                                    backgroundColor: darkMode ? "#1f1f1f" : "#ffffff",
                                    borderRadius: 8
                                }}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: darkMode ? "#1a1a1a" : "#ffffff",
                        color: darkMode ? "#f0f0f0" : "#000000",
                        padding: "40px"
                    }}
                >
                    <div
                        style={{
                            textAlign: "center",
                            maxWidth: "500px"
                        }}
                    >
                        <h3 style={{
                            color: darkMode ? "#f0f0f0" : "#000000",
                            marginBottom: "16px"
                        }}>
                            Veri Bulunamadı
                        </h3>
                        <p style={{
                            color: darkMode ? "#b0b0b0" : "#666666",
                            marginBottom: "24px",
                            lineHeight: "1.6"
                        }}>
                            Pivot tabloya geçmek için önce Fatura sayfasından bir liste seçin ve
                            "Pivot Tabloya Git" butonuna tıklayın.
                        </p>
                        <button
                            onClick={() => window.history.back()}
                            style={{
                                padding: "12px 24px",
                                backgroundColor: darkMode ? "#2d2d2d" : "#f0f0f0",
                                color: darkMode ? "#f0f0f0" : "#000000",
                                border: `1px solid ${darkMode ? "#555555" : "#d9d9d9"}`,
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight: "500"
                            }}
                            onMouseOver={(e) => {
                                e.target.style.backgroundColor = darkMode ? "#3c3c3c" : "#e6e6e6";
                            }}
                            onMouseOut={(e) => {
                                e.target.style.backgroundColor = darkMode ? "#2d2d2d" : "#f0f0f0";
                            }}
                        >
                            Geri Dön
                        </button>
                    </div>
                </div>
            )}

            {/* Save Modal */}
            <Modal
                title="Pivot Tabloyu Kaydet"
                open={saveModalVisible}
                onOk={handleSavePivot}
                onCancel={() => setSaveModalVisible(false)}
                confirmLoading={saving}
                okText="Kaydet"
                cancelText="İptal"
            >
                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", marginBottom: 8 }}>Başlık</label>
                    <Input
                        value={saveTitle}
                        onChange={(e) => setSaveTitle(e.target.value)}
                        placeholder="Pivot tablo başlığı girin"
                    />
                </div>
                <div>
                    <label style={{ display: "block", marginBottom: 8 }}>Grafik Tipi</label>
                    <Select
                        value={chartType}
                        onChange={setChartType}
                        style={{ width: "100%" }}
                        placeholder="Grafik tipi seçin"
                    >
                        <Option value="flat">📊 Tablo Görünümü</Option>
                        <Option value="pie">🥧 Pasta Grafik</Option>
                        <Option value="bar_h">📊 Yatay Bar Grafik</Option>
                        <Option value="column">📊 Kolon Grafik</Option>
                        <Option value="line">📈 Çizgi Grafik</Option>
                        <Option value="area">📉 Alan Grafik</Option>
                    </Select>
                </div>
            </Modal>

            {/* Load Modal */}
            <Modal
                title="Kaydedilmiş Pivot Tablolar"
                open={loadModalVisible}
                onCancel={() => setLoadModalVisible(false)}
                footer={null}
                width={700}
            >
                <Spin spinning={loading}>
                    {savedPivots.length === 0 ? (
                        <div style={{
                            textAlign: "center",
                            padding: "40px",
                            color: darkMode ? "#b0b0b0" : "#666666"
                        }}>
                            <p>Henüz kaydedilmiş pivot tablonuz yok.</p>
                        </div>
                    ) : (
                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            {savedPivots.map((pivot) => (
                                <div
                                    key={pivot.id}
                                    style={{
                                        padding: "12px 16px",
                                        marginBottom: "8px",
                                        border: `1px solid ${darkMode ? "#555555" : "#d9d9d9"}`,
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        backgroundColor: darkMode ? "#2d2d2d" : "#fafafa",
                                        transition: "all 0.2s"
                                    }}
                                    onClick={() => loadPivotById(pivot.id)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = darkMode ? "#3c3c3c" : "#e6e6e6";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = darkMode ? "#2d2d2d" : "#fafafa";
                                    }}
                                >
                                    <div style={{
                                        fontSize: "16px",
                                        fontWeight: "500",
                                        color: darkMode ? "#f0f0f0" : "#000000",
                                        marginBottom: "4px"
                                    }}>
                                        {pivot.title}
                                    </div>
                                    <InlinePivotChart pivotId={pivot.id} darkMode={darkMode} />
                                </div>
                            ))}
                        </div>
                    )}
                </Spin>
            </Modal>
        </div>
    );
}

export default PivotPage;